import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Paper, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Ramp } from "../../../../../../../types";
import { getRampDimensionsFromCalibration } from "../../../../../../../utils/calibrationPointCalculations";
import { useSetupWizardStore } from "../useSetupWizardStore";

type Props = {
  ramp: Ramp;
  setRamp: (updater: (prev: Ramp) => Ramp) => void;
  onComplete: () => void;
  /** Persist ramp to database (auto-save on wizard complete) */
  onSave: (ramp: Ramp) => void;
};

export const ConfirmStep: React.FC<Props> = ({ ramp, setRamp, onComplete, onSave }) => {
  const { t } = useTranslation();
  const { prevStep, getCompletedPoints, getCalibrationPoints, exitWizard } =
    useSetupWizardStore();
  const [isApplying, setIsApplying] = React.useState(false);

  const completedPoints = getCompletedPoints();

  const [estimatedWidth, setEstimatedWidth] = React.useState<number | null>(null);
  const [estimatedDepth, setEstimatedDepth] = React.useState<number | null>(null);

  React.useEffect(() => {
    const calculateDimensions = async () => {
      if (completedPoints.length >= 2) {
        const result = await getRampDimensionsFromCalibration(completedPoints, ramp);
        setEstimatedWidth(result.width);
        setEstimatedDepth(result.depth);
      }
    };
    calculateDimensions();
  }, [completedPoints, ramp]);

  const formatDimension = (value: number | null): string => {
    if (value === null) return "—";
    const rounded = Math.round(value);
    if (rounded > 10000) return "—";
    return `${rounded} ft`;
  };

  const handleApplyAndFinish = async () => {
    setIsApplying(true);

    try {
      const calibrationPoints = getCalibrationPoints(ramp.id);

      const updates: Partial<Ramp> = {
        calibration_points: calibrationPoints,
      };

      if (estimatedWidth !== null && estimatedWidth > 0 && estimatedWidth <= 10000) {
        updates.width = Math.round(estimatedWidth);
      }
      if (estimatedDepth !== null && estimatedDepth > 0 && estimatedDepth <= 10000) {
        updates.depth = Math.round(estimatedDepth);
      }

      const updatedRamp = { ...ramp, ...updates };

      // Update local state
      setRamp(() => updatedRamp);

      // Auto-save to database immediately
      onSave(updatedRamp);

      exitWizard();
      onComplete();
    } catch (error) {
      console.error("Failed to apply calibration:", error);
      setIsApplying(false);
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 24,
        right: 24,
        pointerEvents: "auto",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 2,
          minWidth: 280,
        }}
      >
        {/* Compact header with dimensions inline */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {formatDimension(estimatedWidth)}
          </Typography>
          <Typography variant="body2" color="text.secondary">×</Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDimension(estimatedDepth)}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={prevStep}
            disabled={isApplying}
          >
            {t("common.back", "Back")}
          </Button>

          <Button
            variant="contained"
            size="small"
            endIcon={<CheckIcon />}
            onClick={handleApplyAndFinish}
            disabled={isApplying}
            sx={{ flex: 1 }}
          >
            {isApplying
              ? t("common.applying", "...")
              : t("settings.rampsSettings.setupWizard.confirm.finish", "Start Editing")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
